import { useState, useCallback } from 'react'
import { ChevronDown, ChevronRight, Loader2 } from 'lucide-react'
import { useAuditTrail } from '@/hooks/useAuditTrail'
import { useCurrentVersionId } from '@/hooks/useCurrentVersionId'
import { Pagination } from '@/components/common/Pagination'
import { usePersistedPageSize } from '@/hooks/usePersistedPageSize'
import { TABLE_PAGE_SIZE_KEYS } from '@/stores/useTablePageSizeStore'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { useDagTableStore } from '@/stores/useDagTableStore'
import {
  formatAuditTitle,
  formatActorDisplay,
  formatAuditTime,
  formatChangeSummary,
} from '@/utils/auditTrailFormatters'
import { formatDateShort } from '@/utils/formatters'
import type { ChangeLogEntry } from '@/types'

const PAGE_SIZE = 20

interface PlanHistoryTabProps {
  planId: string
}

function ChangesList({ changes }: { changes: ChangeLogEntry['changes'] }) {
  const [expanded, setExpanded] = useState(false)

  if (changes.length === 0) return null

  if (changes.length === 1) {
    return (
      <p className="text-xs text-muted-foreground truncate">
        {formatChangeSummary(changes[0])}
      </p>
    )
  }

  return (
    <div>
      <button
        type="button"
        onClick={e => {
          e.stopPropagation()
          setExpanded(v => !v)
        }}
        className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground cursor-pointer"
      >
        {expanded ? (
          <ChevronDown className="h-3 w-3" />
        ) : (
          <ChevronRight className="h-3 w-3" />
        )}
        {changes.length} fields changed
      </button>
      {expanded && (
        <ul className="mt-0.5 space-y-0.5 pl-4">
          {changes.map((change, i) => (
            <li key={i} className="text-xs text-muted-foreground truncate">
              {formatChangeSummary(change)}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function HistoryEntry({ entry }: { entry: ChangeLogEntry }) {
  const highlightedRowId = useDagTableStore(s => s.highlightedRowId)
  const setHighlightedRowId = useDagTableStore(s => s.setHighlightedRowId)
  const isLineItem = entry.entity_type === 'LINE_ITEM'
  const isHighlighted = isLineItem && highlightedRowId === entry.entity_id

  const handleClick = useCallback(() => {
    if (!isLineItem) return
    setHighlightedRowId(isHighlighted ? null : entry.entity_id)
  }, [isLineItem, isHighlighted, entry.entity_id, setHighlightedRowId])

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div
          onClick={handleClick}
          className={`flex items-start gap-3 rounded-md px-2 py-1.5 transition-colors ${
            isLineItem ? 'cursor-pointer hover:bg-muted/60' : ''
          } ${isHighlighted ? 'bg-primary/10' : ''}`}
        >
          <div className="mt-2 h-2 w-2 shrink-0 rounded-full bg-[#d1d5dc]" />
          <div className="min-w-0 space-y-0.5">
            <p className="text-sm font-medium">{formatAuditTitle(entry)}</p>
            <ChangesList changes={entry.changes} />
            <p className="text-xs text-muted-foreground">
              {formatActorDisplay(entry.actor)} &middot;{' '}
              {formatDateShort(entry.timestamp)}
            </p>
          </div>
        </div>
      </TooltipTrigger>
      <TooltipContent side="left" className="text-xs">
        <p>
          {formatDateShort(entry.timestamp)} at{' '}
          {formatAuditTime(entry.timestamp)}
        </p>
      </TooltipContent>
    </Tooltip>
  )
}

export function PlanHistoryTab({ planId }: PlanHistoryTabProps) {
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = usePersistedPageSize(
    TABLE_PAGE_SIZE_KEYS.PLAN_HISTORY,
    PAGE_SIZE
  )

  const { versionId } = useCurrentVersionId(planId)
  const { data, isLoading, isError } = useAuditTrail(
    planId,
    versionId,
    page,
    pageSize
  )

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (isError) {
    return (
      <p className="py-8 text-center text-sm text-muted-foreground">
        Failed to load history. Please try again.
      </p>
    )
  }

  if (!data || data.data.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-muted-foreground">
        No history available for this plan.
      </p>
    )
  }

  const totalPages = Math.ceil(data.pagination.total / pageSize)

  return (
    <TooltipProvider>
      <div className="space-y-1">
        {data.data.map(entry => (
          <HistoryEntry key={entry.id} entry={entry} />
        ))}

        {totalPages > 1 && (
          <Pagination
            page={page}
            pageSize={pageSize}
            totalPages={totalPages}
            onPageChange={setPage}
            onPageSizeChange={size => {
              setPageSize(size)
              setPage(1)
            }}
            pageSizeOptions={[10, 20, 50]}
          />
        )}
      </div>
    </TooltipProvider>
  )
}
